export interface ColorBand {
  value: number;
  color: string;
}

let COLORS = [
  "#A3605D",
  "#D6805A",
  "#F3D59E",
  "#FEFFCD",
  "#CEDDB0",
  "#99BA8D",
  "#5F8B6C",
  "#395746",
];

export const generateColorBands = (
  min: number,
  max: number,
  inverted: boolean = false,
  neutral = 100,
): ColorBand[] => {
  min = Math.min(min, neutral);
  max = Math.max(max, neutral);

  const lowerValues = generateColorSteps(min, neutral, 3);
  const upperValues = generateColorSteps(neutral, max, 4);
  let colors = COLORS;
  if (inverted == true) {
    colors = [...COLORS].reverse();
  }
  return [
    { value: lowerValues[0], color: colors[0] },
    { value: lowerValues[1], color: colors[1] },
    { value: lowerValues[2], color: colors[2] },
    { value: lowerValues[3], color: colors[3] },

    { value: upperValues[1], color: colors[4] },
    { value: upperValues[2], color: colors[5] },
    { value: upperValues[3], color: colors[6] },
    { value: upperValues[4], color: colors[7] },
  ];
};

const generateColorSteps = (
  start: number,
  end: number,
  intervals: number,
): number[] => {
  const distance = end - start;
  const baseStep = Math.floor(distance / intervals);
  let remainder = distance % intervals;
  const result = [start];
  let current = start;
  for (let i = 0; i < intervals; i++) {
    current += baseStep;

    if (remainder > 0) {
      current += 1;
      remainder--;
    }

    result.push(current);
  }

  return result;
};

export const getColorFromBands = (
  value: number,
  bands: ColorBand[],
): string => {
  if (value <= bands[0].value) {
    return bands[0].color;
  }
  if (value >= bands[bands.length - 1].value) {
    return bands[bands.length - 1].color;
  }

  for (let i = 0; i < bands.length - 1; i++) {
    const current = bands[i];
    const next = bands[i + 1];

    if (value >= current.value && value <= next.value) {
      const factor = (value - current.value) / (next.value - current.value);

      return interpolateColor(current.color, next.color, factor);
    }
  }

  return bands[3].color;
};

const interpolateColor = (
  color1: string,
  color2: string,
  factor: number,
): string => {
  factor = Math.max(0, Math.min(1, factor));

  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  const r = Math.round(c1.r + (c2.r - c1.r) * factor);

  const g = Math.round(c1.g + (c2.g - c1.g) * factor);

  const b = Math.round(c1.b + (c2.b - c1.b) * factor);

  return rgbToHex(r, g, b);
};

function hexToRgb(hex: string) {
  const h = hex.replace("#", "");

  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

export const getTextColor = (backgroundColor: string): string => {
  const { r, g, b } = hexToRgb(backgroundColor);

  // Perceived brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness >= 150 ? "#333333" : "#FFFFFF";
};
